import React, { useState, useMemo } from 'react';
import { Box, Popover, Typography, Select, MenuItem, FormControl, InputLabel, FormHelperText, SelectChangeEvent } from '@mui/material';
import { IconUser } from '@/icons';
import BaseFilterPill from './BaseFilterPill';
import { FilterComponentProps } from './UserProfileFilters';
import { RoleFilter } from '@/pages/user/types';
import { useAvailableRoles } from '../../hooks/useAvailableRoles';
import { roleDefinitions } from '../../roleDefinitions';
import { RoleChip } from '../../components/RoleChip';

interface RoleFilterPillProps extends FilterComponentProps {
  value: RoleFilter | undefined;
  onChange: (value: RoleFilter) => void;
  organizationId?: string;
  courseId?: string;
  roleType?: string;
}

const RoleFilterPill: React.FC<RoleFilterPillProps> = ({ 
  value = { roleType: 'admin' }, 
  onChange,
  onRemove,
  organizationId: contextOrgId,
  courseId: contextCourseId,
  roleType
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { organizations } = useAvailableRoles(contextOrgId, contextCourseId);
  
  // Ensure value has roleType set (in case it's missing)
  const currentValue = { ...value, roleType: value.roleType || roleType };

  // Determine the scope of this role
  const roleScope = roleDefinitions[currentValue.roleType]?.scope || 'global';
  
  // Get available organizations and courses based on selected organization
  const availableOrgs = organizations;
  const availableCourses = currentValue.organizationId 
    ? organizations.find(org => org.id === currentValue.organizationId)?.courses || []
    : [];

  // When in context, use the context values
  const effectiveOrgId = contextOrgId || currentValue.organizationId;
  const effectiveCourseId = contextCourseId || currentValue.courseId;

  const handleOpenPicker = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
  };

  const handleOrgChange = (event: SelectChangeEvent) => {
    const orgId = event.target.value as string;
    
    // Clear courseId if org changes
    if (orgId !== currentValue.organizationId) {
      onChange({
        ...currentValue,
        organizationId: orgId || undefined,
        courseId: undefined
      });
    } else {
      onChange({
        ...currentValue,
        organizationId: orgId || undefined
      });
    }
  };

  const handleCourseChange = (event: SelectChangeEvent) => {
    const courseId = event.target.value as string;
    onChange({
      ...currentValue,
      courseId: courseId || undefined
    });
  };

  const getSelectorText = () => {
    // For organization-scoped roles
    if (roleScope === 'organization' || roleScope === 'course') {
      if (!effectiveOrgId) {
        return 'Any Organization';
      }
      
      // If in organization or course context, don't show organization name for org-scoped roles
      if ((contextOrgId && roleScope === 'organization') || contextCourseId) {
        return '';
      }
      
      const orgName = availableOrgs.find(o => o.id === effectiveOrgId)?.name;

      // For course-scoped roles, add course info
      if (roleScope === 'course') {
        if (!effectiveCourseId) {
          // In course context, don't show the course selector text
          if (contextCourseId) {
            return '';
          }
          return `${orgName || 'Organization'} / Any Course`;
        }
        
        // In course context, don't show course name
        if (contextCourseId) {
          return '';
        }
        
        const courseName = availableCourses.find(c => c.id === effectiveCourseId)?.name;
        return `${orgName || 'Organization'} / ${courseName || 'Course'}`;
      }
      
      return orgName || 'Organization';
    }
    
    return '';  // Global roles don't need additional text
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <BaseFilterPill
        icon={<IconUser fontSize="small" />}
        label=""
        onRemove={onRemove}
        background="rgba(0, 0, 0, 0.04)" // More neutral background
      >
        <Box 
          sx={{ 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          }} 
          onClick={handleOpenPicker}
        >
          <RoleChip roleId={currentValue.roleType} />
          
          {getSelectorText() && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {getSelectorText()}
            </Typography>
          )}
        </Box>
      </BaseFilterPill>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <RoleChip roleId={currentValue.roleType} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ ml: 1 }}>
              Filter
            </Typography>
          </Box>
          
          {/* Show organization selector for org/course scoped roles, but only if not in organization or course context */}
          {(roleScope === 'organization' || roleScope === 'course') && !contextOrgId && !contextCourseId && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="org-select-label">Organization</InputLabel>
              <Select
                labelId="org-select-label"
                value={currentValue.organizationId || ''}
                label="Organization"
                onChange={handleOrgChange}
              >
                <MenuItem value="">
                  <em>Any Organization</em>
                </MenuItem>
                {availableOrgs.map(org => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select organization or "Any"</FormHelperText>
            </FormControl>
          )}
          
          {/* Show course selector for course scoped roles, but not in course context */}
          {roleScope === 'course' && effectiveOrgId && !contextCourseId && (
            <FormControl fullWidth size="small">
              <InputLabel id="course-select-label">Course</InputLabel>
              <Select
                labelId="course-select-label"
                value={currentValue.courseId || ''}
                label="Course"
                onChange={handleCourseChange}
              >
                <MenuItem value="">
                  <em>Any Course</em>
                </MenuItem>
                {availableCourses.map(course => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select specific course or "Any"</FormHelperText>
            </FormControl>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default RoleFilterPill; 